import { useState, useCallback } from 'react';

export const useLocalStorage = (key, initialValue) => {
    // Estado para almacenar nuestro valor
    // Pasa la función inicial al useState para que solo se ejecute una vez
    const [storedValue, setStoredValue] = useState(() => {
        try {
            const item = window.localStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        } catch (error) {
            console.error(`Error reading localStorage key "${key}":`, error);
            return initialValue;
        }
    });

    // Función para establecer el valor.
    // Memoizada con useCallback para que la referencia sea estable entre
    // renders — si no, cualquier useEffect que dependa de este setter se
    // re-ejecutaría en cada render y, si dentro hace setState, dispara un
    // loop "Maximum update depth exceeded".
    const setValue = useCallback((value) => {
        try {
            setStoredValue((prev) => {
                const valueToStore = value instanceof Function ? value(prev) : value;
                try {
                    window.localStorage.setItem(key, JSON.stringify(valueToStore));
                } catch (error) {
                    console.error(`Error setting localStorage key "${key}":`, error);
                }
                return valueToStore;
            });
        } catch (error) {
            console.error(`Error setting localStorage key "${key}":`, error);
        }
    }, [key]);

    const removeValue = useCallback(() => {
        try {
            setStoredValue(initialValue);
            window.localStorage.removeItem(key);
        } catch (error) {
            console.error(`Error removing localStorage key "${key}":`, error);
        }
    }, [key, initialValue]);

    const clearAll = useCallback(() => {
        try {
            window.localStorage.clear();
            setStoredValue(initialValue);
        } catch (error) {
            console.error('Error clearing localStorage:', error);
        }
    }, [initialValue]);

    return [storedValue, setValue, removeValue, clearAll];
};
